import { list, named, num, text, toggle, url, type Choice } from '../../themes/settingFields';
import { element } from './definition';
import { focus, hairline, look, pad, paint, radius, round, space, stack, strip, typeface } from './styleTokens';

/** What one box accepts, under the words a merchant reads rather than the HTML input types. */
const INPUT_TYPES: readonly Choice[] = [
  ['text', 'Text'], ['email', 'Email address'], ['tel', 'Phone number'], ['number', 'Number'], ['password', 'Password']
];

// The controls a visitor fills in, and the box that gathers them. Only the `form` takes children —
// everything else here is a leaf, so the drag layer treats a field as one target rather than a box.
// Each control lands with a focus state, because a field a keyboard cannot see itself in is unfinished.
//
// A field the merchant can pre-answer is `defaultValue`, the one control LightFunnels puts under every
// typed field and we had nowhere for: a city a single-town shop always ships to, a country code in
// front of a phone. It is what the box starts holding, never what it must end up holding.
//
// The two length bounds are not new behaviour: `fieldRuleOf` has always registered `minLength` and
// `maxLength`, and `checkLength` has always scored an answer against them — no control had ever
// declared them, so the one rule a merchant most often wants on a written answer was unreachable.

// Describes the shortest and longest a written answer may be; leave them at 0 and nothing is checked.
const lengthFields = () => [num('minLength', 'Shortest answer', 0, 500), num('maxLength', 'Longest answer', 0, 5000)];

// Describes the resting look every field in this file shares, so a form reads as one set rather than four.
const fieldLook = () => ({
  width: '100%', fontFamily: typeface('body'), fontSize: '15px', lineHeight: 1.2, color: paint('text'),
  backgroundColor: paint('surface'), ...pad('12px', '16px'), ...hairline(), ...radius(round('sm'))
});

// Describes what a field looks like once the keyboard is in it.
const fieldFocus = () => ({ borderColor: paint('primary'), boxShadow: '0 0 0 3px rgba(17, 17, 17, 0.08)' });

/** The box that gathers fields and posts them — a lead capture, an enquiry, a competition entry. */
export const formElement = element('form', {
  label: 'Form',
  description: 'The box that gathers the fields inside it and sends them when the button is pressed.',
  category: 'Forms',
  icon: 'clipboard-list',
  props: [text('name', 'Form name'), text('successMessage', 'Thank-you message'), { ...text('errorMessage', 'Error message'), help: 'Shown when the answers could not be sent. A field the visitor got wrong says why under itself.' }, url('href', 'Where to send them after')],
  defaults: {
    props: {
      name: 'Contact', successMessage: 'Thank you — we have your details.',
      errorMessage: 'Your answers could not be sent. Please try again.', href: ''
    },
    styles: look({ ...stack(space('sm')), width: '100%' })
  },
});

/** One line a visitor types into. */
export const inputElement = element('input', {
  label: 'Input',
  description: 'A single line a visitor types into — a name, a phone number, an email address.',
  category: 'Forms',
  icon: 'text-cursor-input',
  props: [
    text('name', 'Field name'), text('label', 'Label'),
    named('inputType', 'Type', INPUT_TYPES),
    text('placeholder', 'Placeholder'), text('defaultValue', 'Default Value'), toggle('required', 'Required'),
    ...lengthFields()
  ],
  defaults: {
    props: {
      name: 'fullName', label: 'Full name', placeholder: 'Your full name', defaultValue: '',
      inputType: 'text', required: false, minLength: 0, maxLength: 0
    },
    styles: focus(look(fieldLook()), fieldFocus())
  }
});

/** Several lines a visitor types into. */
export const textareaElement = element('textarea', {
  label: 'Textarea',
  description: 'Several lines a visitor types into — a message, an order note, a review.',
  category: 'Forms',
  icon: 'align-left',
  props: [
    text('name', 'Field name'), text('label', 'Label'), text('placeholder', 'Placeholder'),
    text('defaultValue', 'Default Value'), num('rows', 'Lines shown', 2, 20), toggle('required', 'Required'),
    ...lengthFields()
  ],
  defaults: {
    props: {
      name: 'message', label: 'Message', placeholder: 'Anything we should know?', defaultValue: '',
      rows: 4, required: true, minLength: 0, maxLength: 0
    },
    styles: focus(look({ ...fieldLook(), minHeight: '100px', lineHeight: 1.6 }), fieldFocus())
  }
});

/** A closed list of answers the visitor picks one of. */
export const selectElement = element('select', {
  label: 'Select',
  description: 'A closed list of answers the visitor picks one of — a city, a size, a delivery window.',
  category: 'Forms',
  icon: 'chevrons-up-down',
  props: [
    text('name', 'Field name'), text('label', 'Label'), text('placeholder', 'Placeholder'),
    list('options', 'Options', [text('label', 'Label'), text('value', 'Value')]), toggle('required', 'Required')
  ],
  defaults: {
    props: {
      name: 'choice', label: 'Choose one', placeholder: 'Please choose…',
      // Two starter answers, so the control opens as a demonstrable dropdown instead of a bare placeholder.
      options: [{ label: 'Option 1', value: 'option-1' }, { label: 'Option 2', value: 'option-2' }],
      required: true
    },
    styles: focus(look({ ...fieldLook(), fontWeight: 500, paddingLeft: '12px', paddingRight: '12px', cursor: 'pointer' }), fieldFocus())
  }
});

/** A single yes-or-no the visitor ticks. */
export const checkboxElement = element('checkbox', {
  label: 'Checkbox',
  description: 'A single box the visitor ticks — an agreement, an opt-in, an extra they want adding.',
  category: 'Forms',
  icon: 'square-check',
  props: [text('name', 'Field name'), text('label', 'Content'), toggle('checked', 'Default value'), toggle('required', 'Required')],
  defaults: {
    // The gap and the ticked colour are LightFunnels' own two checkbox settings, kept where every other
    // colour in this catalog lives — a style bound to a token, so a repaint reaches the tick as well.
    props: { name: 'agree', label: 'Check me', checked: false, required: false },
    styles: focus(look({ ...strip('18px'), fontSize: '15px', color: paint('text'), cursor: 'pointer' }), { color: paint('primary') })
  }
});

/** The caption above a field, when the field's own label is not where it should sit. */
export const labelElement = element('label', {
  label: 'Label',
  description: 'The caption above a field, for when you want it somewhere the field cannot put it.',
  category: 'Forms',
  icon: 'tag',
  props: [text('text', 'Content'), text('htmlFor', 'Field it names')],
  defaults: {
    props: { text: 'Label', htmlFor: '' },
    styles: look({
      display: 'block', fontFamily: typeface('body'), fontSize: '15px', fontWeight: 600,
      lineHeight: 1.2, color: paint('heading'), marginBottom: '6px'
    })
  }
});

/**
 * A row of stars — a rating being given, or one being shown.
 *
 * It lands as the question, not as the answer: this is a field in the Forms group, and a field that
 * arrives already filled in with five stars nobody gave is both a dead input and an invented shop
 * fact. A preset that wants the read-only row asks for it by name, as `starsNode` does.
 */
export const starsElement = element('stars', {
  label: 'Stars',
  description: 'A row of stars, either asking the visitor for a score or showing one.',
  category: 'Forms',
  icon: 'star',
  props: [text('name', 'Field name'), text('label', 'Label'), num('rating', 'Stars filled', 0, 5), num('max', 'Stars shown', 3, 10), toggle('readOnly', 'Show only, do not ask')],
  defaults: {
    props: { name: 'rating', label: 'Your rating', rating: 0, max: 5, readOnly: false },
    styles: look({ ...strip('4px'), fontSize: '20px', color: paint('accent'), cursor: 'pointer' })
  }
});

/** The box a visitor drops a file into — a photograph with a review, a prescription, a design. */
export const fileUploadElement = element('file-upload', {
  label: 'File upload',
  description: 'A box a visitor attaches a file to — a photo with their review, or artwork for their order.',
  category: 'Forms',
  icon: 'upload',
  props: [
    // No accepted-types box. The limits are the shop's own upload route's — JPEG, PNG, WebP, GIF and
    // AVIF — and a type this field accepted that the route refuses is a form that fails at the last
    // step, every time. `FileUploadField` prints what may be attached instead of asking.
    text('name', 'Field name'), text('label', 'Label'),
    text('buttonLabel', 'Button label'), toggle('multiple', 'Allow several'), toggle('required', 'Required')
  ],
  defaults: {
    props: { name: 'attachment', label: 'Add a photo', buttonLabel: 'Choose a file', multiple: false, required: false },
    styles: focus(
      look({
        width: '100%', textAlign: 'center', color: paint('textMuted'), backgroundColor: paint('surfaceAlt'),
        borderStyle: 'dashed', borderWidth: '2px', borderColor: paint('border'), cursor: 'pointer',
        ...pad('16px', '16px'), ...radius(round('sm'))
      }),
      { borderColor: paint('primary'), color: paint('text') }
    )
  }
});
